import * as React from "react";
import { useInView } from "react-intersection-observer";
import cx from "classnames";
import TrendingLanding from "app/discover/components/trendingLanding";
import PulseLoadingDot from "common/components/pulseLoadingDot";
import InfiniteScroller from "@vingle/wtf-sdk/components/infiniteScroller";
import { useEventCallback } from "@vingle/wtf-sdk/helpers/hook";
import Icon from "common/icons";
import SectionContentCard from "./sectionContentCard";
import { NAVBAR_HEIGHT } from "@vingle/wtf-sdk/constants/global";

const styles = require("./trending.scss");
interface IProps {
  isModal: boolean;
  sectionId: string;
  contents: Vingle.IPaginatedList<Vingle.Discover.IDiscoverContent> | null;
  sections: Vingle.Discover.ITrendingSection[];
  isTrendingSectionContentsLoading: boolean;
  isTrendingSectionsLoading: boolean;
  handleLoadContentsData: (
    sectionId: string,
    after?: Vingle.PagingValue,
  ) => void;
  onSelectSection: (section: Vingle.Discover.ITrendingSection) => void;
  onClose: () => void;
}

function TrendingComponent(props: IProps) {
  const {
    isModal,
    sectionId,
    sections,
    contents,
    isTrendingSectionContentsLoading,
    isTrendingSectionsLoading,
    onClose,
    onSelectSection,
    handleLoadContentsData,
  } = props;
  const currentSection = sections.find(section => section.id === sectionId);
  const [triggerRef, isInView] = useInView({
    rootMargin: `-${NAVBAR_HEIGHT}px`,
  });
  const handleLoadMore = React.useCallback(() => {
    handleLoadContentsData(sectionId, contents && contents.paging.after);
  }, [sectionId, handleLoadContentsData, contents && contents.paging.after]);
  return !sections.length && isTrendingSectionsLoading ? (
    <div className={styles.pulseLoadingDotWrapper}>
      <PulseLoadingDot />
    </div>
  ) : (
    <div className={styles.container}>
      {isModal && (
        <div className={styles.closeButton} onClick={onClose}>
          <Icon icon="CLOSE_DIALOG" />
        </div>
      )}
      <div ref={triggerRef}>
        <TrendingLanding
          sections={sections}
          initialActiveSection={currentSection}
          onChangeActiveSection={onSelectSection}
        />
      </div>
      {!contents ||
      (!contents.data.length && isTrendingSectionContentsLoading) ? (
        <div className={styles.pulseLoadingDotWrapper}>
          <PulseLoadingDot />
        </div>
      ) : (
        <div className={styles.wrapper}>
          <MoveSectionButton
            direction="left"
            handleClick={onSelectSection}
            sectionId={sectionId}
            sections={sections}
            inView={!isInView}
          />
          <MoveSectionButton
            direction="right"
            handleClick={onSelectSection}
            sectionId={sectionId}
            sections={sections}
            inView={!isInView}
          />
          <InfiniteScroller
            className={styles.content}
            paging={contents.paging}
            loadMore={handleLoadMore}
            itemLength={contents.data.length}
            threshold={750}
            loader={
              <div className={styles.pulseLoadingDotWrapper}>
                <PulseLoadingDot />
              </div>
            }
          >
            {contents.data.map((content, position) => {
              return (
                <SectionContentCard
                  key={content.url}
                  sectionId={sectionId}
                  content={content}
                  position={position}
                />
              );
            })}
          </InfiniteScroller>
        </div>
      )}
    </div>
  );
}

const MoveSectionButton = React.memo(
  ({
    direction,
    handleClick,
    sectionId,
    sections,
    inView,
  }: {
    direction: "left" | "right";
    handleClick: (section: Vingle.Discover.ITrendingSection) => void;
    sectionId: string;
    sections: Vingle.Discover.ITrendingSection[];
    inView: boolean;
  }) => {
    const currentIndex = sections.findIndex(
      section => sectionId === section.id,
    );
    const targetIndex =
      direction === "left" ? currentIndex - 1 : currentIndex + 1;
    if (
      !sections.length ||
      !(targetIndex > -1 && targetIndex < sections.length)
    ) {
      return null;
    }
    const onClick = useEventCallback(() => {
      handleClick(sections[targetIndex]);
    }, []);

    return (
      <div
        className={cx(
          styles.moveSectionButton,
          { [styles.left]: direction === "left" },
          { [styles.right]: direction === "right" },
          { [styles.inView]: inView },
        )}
        onClick={onClick}
      >
        <div className={styles.moveSectionButtonWrapper}>
          <span>{sections[targetIndex].title}</span>
          <Icon
            icon="DISCOVER_TRENDING_SECTION_ARROW"
            width={20}
            height={20}
            className={styles.icon}
          />
        </div>
      </div>
    );
  },
);

export default TrendingComponent;
